import { reactive, readonly, watch } from 'vue'
import { getPhoneCredentials, identifyCaller, recordCall } from '@/services/wootdashLinkus'
import { applySink, effectiveDevices, supportsOutputSelection } from '@/composables/useAudioDevices'

// Telefone no navegador (Yeastar WebRTC SDK, ys-webrtc-sdk-core): o dashboard entra no PABX como o ramal do
// agente e liga/atende por aqui, sem o Linkus. Um estado só pra tela inteira (o painel flutuante e os botões
// Ligar). O SDK é carregado só quando o agente liga o telefone (é grande e só serve com o Linkus configurado).
// Liga sozinho na próxima vez se o agente deixou ligado (preferência por usuário, no navegador).

export type PhoneState = 'off' | 'connecting' | 'ready' | 'error'

export interface PhoneCall {
  callId: string
  number: string
  name: string
  direction: 'inbound' | 'outbound'
  status: 'ringing' | 'calling' | 'talking' | 'connecting'
  isHold: boolean
  isMute: boolean
  /** ms (Date.now) de quando começou a falar. */
  talkingSince: number | null
  contactId: string | null
  conversationId: string | null
  /** ms de quando a chamada apareceu. */
  startedAt: number
  /** O agente recusou aqui (vira "recusada" no histórico, não "não atendida"). */
  rejected: boolean
}

interface SdkSession {
  status: { number: string; name: string; communicationType: 'inbound' | 'outbound'; callStatus: PhoneCall['status']; isHold: boolean; isMute: boolean }
  remoteStream: MediaStream | null
  on: (event: string, cb: (...args: unknown[]) => void) => unknown
}
interface SdkPhone {
  on: (event: string, cb: (...args: any[]) => void) => unknown
  start: () => unknown
  call: (number: string) => Promise<{ code: number; message: string }>
  answer: (callId: string) => Promise<{ code: number; message: string }>
  reject: (callId: string) => boolean
  hangup: (callId: string) => boolean
  hold: (callId: string) => boolean
  unhold: (callId: string) => boolean
  mute: (callId: string) => boolean
  unmute: (callId: string) => boolean
  dtmf: (callId: string, digit: string) => boolean
  blindTransfer: (callId: string, number: string) => boolean
  getSession: (callId: string) => SdkSession | null
  isRegistered: boolean
  /** O SDK lê isto a cada ligação/atendimento (getUserMedia) — trocar aqui vale da próxima chamada em diante. */
  deviceIds?: { microphoneId?: string; cameraId?: string }
}

const state = reactive({
  state: 'off' as PhoneState,
  problem: null as string | null,
  extension: null as string | null,
  calls: [] as PhoneCall[],
  /** Muda quando o áudio da chamada atual muda — o painel liga no <audio>. */
  remoteStream: null as MediaStream | null,
  panelOpen: false,
  /** Sobe a cada ligação registrada — a aba Recentes recarrega. */
  historyVersion: 0,
})

let phone: SdkPhone | null = null
let destroyFn: (() => void) | null = null
let ringer: { stop: () => void } | null = null
let authToken = ''

const PREF_KEY = (userId: string | number) => `phone.enabled.${userId}`

export function phonePreference(userId: string | number | null | undefined): boolean {
  if (userId == null) return false
  try { return localStorage.getItem(PREF_KEY(userId)) === '1' } catch { return false }
}
function savePreference(userId: string | number | null | undefined, on: boolean) {
  if (userId == null) return
  try { localStorage.setItem(PREF_KEY(userId), on ? '1' : '0') } catch { /* sem storage: só não lembra */ }
}

/** Erro do SDK (código + mensagem) → texto pro agente. */
export function describeSdkError(err: unknown): string {
  const e = err as { code?: number; message?: string; msg?: string } | undefined
  const msg = (e?.message || e?.msg || String(err ?? '')).toLowerCase()
  if (msg.includes('https')) return 'O endereço do PABX precisa ser https.'
  if (msg.includes('linkus') && msg.includes('disab')) return 'O Linkus está desativado pra este ramal no PABX.'
  if (msg.includes('plan')) return 'O plano do PABX não inclui o Linkus SDK.'
  if (msg.includes('elsewhere')) return 'Este ramal entrou em outro navegador — o telefone daqui saiu.'
  if (msg.includes('media') || msg.includes('permission') || msg.includes('notallowed')) return 'O navegador não liberou o microfone. Permita o microfone pra este site e tente de novo.'
  if (msg.includes('network')) return 'Não deu pra conectar no PABX (rede ou endereço).'
  return e?.message || e?.msg || 'Não deu pra ligar o telefone.'
}

/** Toque de chamada (dois bipes) sem arquivo de áudio — WebAudio, até atender/recusar. Com uma saída de toque
 * escolhida (useAudioDevices), sai por ela — ex.: tocar nas caixas de som com a conversa no headset. */
function startRinging() {
  stopRinging()
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    const ctx = new Ctx()
    const ringerId = effectiveDevices.value.ringerId
    let output: AudioNode = ctx.destination
    let ringEl: HTMLAudioElement | null = null
    if (ringerId && supportsOutputSelection) {
      const dest = ctx.createMediaStreamDestination()
      output = dest
      ringEl = new Audio()
      ringEl.srcObject = dest.stream
      void applySink(ringEl, ringerId).then(() => ringEl?.play().catch(() => undefined))
    }
    let stopped = false
    const beep = () => {
      if (stopped) return
      for (const at of [0, 0.25]) {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.frequency.value = 440
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + at)
        gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + at + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.2)
        osc.connect(gain).connect(output)
        osc.start(ctx.currentTime + at)
        osc.stop(ctx.currentTime + at + 0.22)
      }
    }
    beep()
    const timer = setInterval(beep, 2000)
    ringer = {
      stop: () => {
        stopped = true
        clearInterval(timer)
        if (ringEl) { ringEl.pause(); ringEl.srcObject = null }
        ctx.close().catch(() => undefined)
      },
    }
  } catch { /* sem áudio: só o visual */ }
}
function stopRinging() {
  ringer?.stop()
  ringer = null
}

function upsertFromSession(callId: string, session: SdkSession) {
  const s = session.status
  const existing = state.calls.find(c => c.callId === callId)
  const talking = s.callStatus === 'talking'
  if (existing) {
    existing.status = s.callStatus
    existing.isHold = s.isHold
    existing.isMute = s.isMute
    if (talking && !existing.talkingSince) existing.talkingSince = Date.now()
    if (s.name && existing.name === existing.number) existing.name = s.name
  } else {
    state.calls.push({
      callId, number: s.number, name: s.name || s.number, direction: s.communicationType, status: s.callStatus,
      isHold: s.isHold, isMute: s.isMute, talkingSince: talking ? Date.now() : null, contactId: null, conversationId: null,
      startedAt: Date.now(), rejected: false,
    })
  }
  const anyRinging = state.calls.some(c => c.direction === 'inbound' && c.status === 'ringing')
  if (!anyRinging) stopRinging()
}

/** Fim da ligação → histórico + nota na conversa (resultado pelo que aconteceu aqui no navegador). */
function report(c: PhoneCall) {
  if (!authToken || !c.number) return
  const outcome = c.talkingSince ? 'Answered' : c.direction === 'outbound' ? 'NoAnswer' : c.rejected ? 'Rejected' : 'Missed'
  void recordCall(authToken, {
    clientCallId: c.callId, number: c.number, direction: c.direction === 'outbound' ? 'Outbound' : 'Inbound', outcome,
    startedAt: new Date(c.startedAt).toISOString(), answeredAt: c.talkingSince ? new Date(c.talkingSince).toISOString() : null,
    endedAt: new Date().toISOString(), conversationId: c.conversationId,
  }).then(() => { state.historyVersion++ })
}

function removeCall(callId: string) {
  const ended = state.calls.find(c => c.callId === callId)
  if (ended) report(ended)
  state.calls = state.calls.filter(c => c.callId !== callId)
  if (!state.calls.some(c => c.direction === 'inbound' && c.status === 'ringing')) stopRinging()
  if (!state.calls.length) state.remoteStream = null
}

function watchSession(callId: string, session: SdkSession, token: string) {
  upsertFromSession(callId, session)
  session.on('statusChange', () => upsertFromSession(callId, session))
  const setAudio = () => { if (session.remoteStream) state.remoteStream = session.remoteStream }
  session.on('streamAdded', setAudio)
  session.on('updateRemoteStream', setAudio)
  session.on('confirmed', () => { upsertFromSession(callId, session); setAudio() })
  session.on('ended', () => removeCall(callId))
  session.on('failed', () => removeCall(callId))
  // Quem é: contato e conversa (nome do PABX fica se não achar).
  const call = state.calls.find(c => c.callId === callId)
  if (call && !call.contactId) {
    identifyCaller(token, call.number).then(info => {
      const c = state.calls.find(x => x.callId === callId)
      if (!c || !info.found) return
      c.contactId = info.contactId ?? null
      c.conversationId = info.conversationId ?? null
      if (info.contactName) c.name = info.contactName
    })
  }
}

/** Liga o telefone: pega a assinatura no servidor, carrega o SDK e registra o ramal. */
export async function connectPhone(token: string, userId?: string | number | null) {
  if (state.state === 'connecting' || state.state === 'ready') return
  state.state = 'connecting'
  state.problem = null
  authToken = token
  savePreference(userId, true)
  const creds = await getPhoneCredentials(token)
  if (!creds.ok || !creds.secret || !creds.pbxUrl || !creds.extension) {
    state.state = 'error'
    state.problem = creds.problem ?? 'Não deu pra ligar o telefone.'
    return
  }
  try {
    const sdk = await import(/* webpackChunkName: "ys-webrtc" */ 'ys-webrtc-sdk-core')
    const microphoneId = effectiveDevices.value.microphoneId || undefined
    const operator = await sdk.init({
      username: creds.extension, secret: creds.secret, pbxURL: creds.pbxUrl, userAgent: 'WebClient', enableLog: false,
      deviceIds: { microphoneId },
    })
    phone = operator.phone as unknown as SdkPhone
    destroyFn = operator.destroy
    state.extension = creds.extension

    phone.on('isRegisteredChange', (registered: boolean) => {
      state.state = registered ? 'ready' : state.state === 'off' ? 'off' : 'connecting'
    })
    phone.on('registrationFailed', (err: unknown) => {
      state.state = 'error'
      state.problem = describeSdkError(err)
    })
    phone.on('newRTCSession', ({ callId, session }: { callId: string; session: SdkSession }) => watchSession(callId, session, token))
    phone.on('incoming', ({ callId, session }: { callId: string; session: SdkSession }) => {
      upsertFromSession(callId, session)
      state.panelOpen = true
      startRinging()
    })
    phone.on('deleteSession', ({ callId }: { callId: string }) => removeCall(callId))
    operator.pbx.on('runtimeError', (err: unknown) => {
      state.problem = describeSdkError(err)
      state.state = 'error'
    })
    phone.start()
  } catch (err) {
    state.state = 'error'
    state.problem = describeSdkError(err)
    phone = null
  }
}

/** Desliga o telefone daqui (as ligações voltam a tocar só no Linkus/telefone IP). */
export function disconnectPhone(userId?: string | number | null) {
  savePreference(userId, false)
  stopRinging()
  try { destroyFn?.() } catch { /* já desligado */ }
  phone = null
  destroyFn = null
  state.state = 'off'
  state.problem = null
  state.calls = []
  state.remoteStream = null
}

// Trocou o microfone com o telefone ligado: o SDK pega da próxima ligação/atendimento, sem reconectar o ramal.
watch(() => effectiveDevices.value.microphoneId, id => {
  if (phone) phone.deviceIds = { ...(phone.deviceIds ?? {}), microphoneId: id || undefined }
})

function need(): SdkPhone {
  if (!phone || state.state !== 'ready') throw new Error('O telefone do navegador não está ligado.')
  return phone
}

export async function placeCall(number: string, meta?: { name?: string; conversationId?: string | null }) {
  const p = need()
  state.panelOpen = true
  const result = await p.call(number)
  if (result && result.code !== 0 && result.code !== undefined) throw new Error(describeSdkError(result))
  // O nome da conversa já é conhecido: mostra ele em vez de esperar o PABX.
  setTimeout(() => {
    const c = state.calls.find(x => x.number === number && x.direction === 'outbound')
    if (c && meta?.name) c.name = meta.name
    if (c && meta?.conversationId) c.conversationId = meta.conversationId
  }, 300)
}

export async function answerCall(callId: string) {
  stopRinging()
  const result = await need().answer(callId)
  if (result && result.code !== 0 && result.code !== undefined) throw new Error(describeSdkError(result))
}
export const rejectCall = (callId: string) => {
  stopRinging()
  const c = state.calls.find(x => x.callId === callId)
  if (c) c.rejected = true
  need().reject(callId)
  removeCall(callId)
}
export const hangupCall = (callId: string) => { need().hangup(callId) }
export const toggleHold = (c: PhoneCall) => { const p = need(); if (c.isHold) p.unhold(c.callId); else p.hold(c.callId) }
export const toggleMute = (c: PhoneCall) => { const p = need(); if (c.isMute) p.unmute(c.callId); else p.mute(c.callId) }
export const sendDtmf = (callId: string, digit: string) => need().dtmf(callId, digit)
export const transferCall = (callId: string, number: string) => need().blindTransfer(callId, number)
export const setPanelOpen = (open: boolean) => { state.panelOpen = open }

export function useBrowserPhone() {
  return { phone: readonly(state) }
}

/** Só pra testes. */
export function __resetPhone() {
  disconnectPhone()
  state.panelOpen = false
}
